import { useCallback, useMemo, useRef } from "react";
import React from "react";
import { useFragment } from "react-relay";

import { faCopy, faEllipsisV, faPencil, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { graphql } from "relay-runtime";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Avatar, toast } from "~/ui";
import { Tooltip } from "~/ui";
import { Button } from "~/ui";
import { useModals } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { useOpenEditModal } from "./modal/CreateAndEdit";
import type { RuleInput } from "./modal/types";

import type { AutoRecordRulesListItemAppliesTo$key } from "~/__generated__/AutoRecordRulesListItemAppliesTo.graphql";
import type {
	AutoRecordRulesListItemDeleteAutoRecordRuleMutation,
	AutoRecordRulesListItemDeleteAutoRecordRuleMutation$data,
} from "~/__generated__/AutoRecordRulesListItemDeleteAutoRecordRuleMutation.graphql";
import type { AutoRecordRulesListItemDuplicateAutoRecordRule$key } from "~/__generated__/AutoRecordRulesListItemDuplicateAutoRecordRule.graphql";
import type { AutoRecordRulesListItemDuplicateAutoRecordRuleMutation } from "~/__generated__/AutoRecordRulesListItemDuplicateAutoRecordRuleMutation.graphql";
import type {
	AutoRecordRulesListItemRule$data,
	AutoRecordRulesListItemRule$key,
} from "~/__generated__/AutoRecordRulesListItemRule.graphql";
import type { AutoRecordRulesListItemRuleDeletionConfirmModal$key } from "~/__generated__/AutoRecordRulesListItemRuleDeletionConfirmModal.graphql";
import type { AutoRecordRulesListItemRuleDescriptionTooltips$key } from "~/__generated__/AutoRecordRulesListItemRuleDescriptionTooltips.graphql";
import type { AutoRecordRulesListItemUserPill$key } from "~/__generated__/AutoRecordRulesListItemUserPill.graphql";
import type { AutoRecordRulesListItemWorkspacePill$key } from "~/__generated__/AutoRecordRulesListItemWorkspacePill.graphql";
import type { CreateAndEditModalAccount$key } from "~/__generated__/CreateAndEditModalAccount.graphql";

import styles from "./AutoRecordRulesListItem.module.css";

interface AutoRecordRulesListItemProps {
	rule: AutoRecordRulesListItemRule$key;
	account: AutoRecordRulesListItemWorkspacePill$key &
		CreateAndEditModalAccount$key &
		AutoRecordRulesListItemRuleDescriptionTooltips$key;
	refetch: () => void;
	secondary?: boolean;
}

type AutoRecordRuleDescriptionRule = Omit<
	AutoRecordRulesListItemRule$data,
	" $fragmentRefs" | " $fragmentType" | " id"
>;

function formatEmailDomainList(emailDomainList: readonly string[]): string {
	const length = emailDomainList.length;
	if (length === 0) {
		return "";
	} else if (length === 1) {
		return `@${emailDomainList[0]}`;
	} else if (length === 2) {
		return `@${emailDomainList[0]} or @${emailDomainList[1]}`;
	} else {
		return `@${emailDomainList[0]} or @${emailDomainList[1]} and ${length - 2} more`;
	}
}

function formatInviteesRange(min: number, max: number): string | undefined {
	if (min > max) {
		return undefined;
	}

	const length = max - min + 1;

	if (length <= 5) {
		return Array.from({ length }, (_, i) => min + i).join(", ");
	} else {
		return `${Array.from({ length: 5 }, (_, i) => min + i).join(", ")} ... ${max}`;
	}
}

export interface RuleDescriptors {
	attendeeType: string;
	attendeesDescriptor?: string;
	keywordsToContain: string;
	keywordsToRestrict: string;
	organizer: string;
}

export function useAutoRecordRuleDescriptors(
	rule: AutoRecordRuleDescriptionRule | RuleInput,
	accountRef: AutoRecordRulesListItemRuleDescriptionTooltips$key,
): RuleDescriptors {
	const fragment = graphql`
		fragment AutoRecordRulesListItemRuleDescriptionTooltips on Account {
			emailDomainList
		}
	`;

	const account = useFragment<AutoRecordRulesListItemRuleDescriptionTooltips$key>(fragment, accountRef);

	const attendeeType = useMemo(() => {
		let attendeeType = "";
		switch (rule.filterAttendeeType) {
			case "EXTERNAL_ONLY":
				// eslint-disable-next-line max-len
				attendeeType = `1 or more invitees with a domain that is not ${formatEmailDomainList(account.emailDomainList)}`;
				break;
			case "INTERNAL_ONLY":
				attendeeType = `All invitees have the domain ${formatEmailDomainList(account.emailDomainList)}`;
				break;
			case "INTERNAL_AND_EXTERNAL":
				attendeeType = "All meetings";
				break;
		}

		return attendeeType;
	}, [account.emailDomainList, rule.filterAttendeeType]);

	const attendeesDescriptor = useMemo(() => {
		if (rule.filterMinimumAttendees != null && rule.filterMaximumAttendees != null) {
			const range = formatInviteesRange(rule.filterMinimumAttendees, rule.filterMaximumAttendees);
			if (range !== undefined) {
				return `Events with ${range} people`;
			}
		} else if (rule.filterMinimumAttendees != null && rule.filterMinimumAttendees >= 0) {
			return `Events with ${rule.filterMinimumAttendees} or more people`;
		} else if (rule.filterMaximumAttendees != null && rule.filterMaximumAttendees >= 2) {
			return `Events with ${rule.filterMaximumAttendees} or less people`;
		}
		return undefined;
	}, [rule.filterMaximumAttendees, rule.filterMinimumAttendees]);

	const organizer = useMemo(() => {
		if (rule.filterOrganizer === "ANYONE") {
			return "Meetings that are organized by anyone";
		}
		return "Meetings that are organized by me";
	}, [rule.filterOrganizer]);

	return {
		attendeeType,
		attendeesDescriptor,
		keywordsToContain: "Event title or invitees email include any of these:",
		keywordsToRestrict: "Event title or invitees email does not include any of these:",
		organizer,
	};
}

export function AutoRecordRuleDescription({
	rule,
	account: accountRef,
	className,
}: {
	rule: AutoRecordRuleDescriptionRule | RuleInput;
	account: AutoRecordRulesListItemRuleDescriptionTooltips$key;
	className?: string;
}) {
	// Example Internal meetings with more than 2 attendees that contains Keyword, @domain.com and does not contain Text
	let attendeeType = "Internal";
	switch (rule.filterAttendeeType) {
		case "EXTERNAL_ONLY":
			attendeeType = "External";
			break;
		case "INTERNAL_ONLY":
			attendeeType = "Internal";
			break;
		case "INTERNAL_AND_EXTERNAL":
			attendeeType = "Internal and External";
			break;
	}

	let attendeesDescriptor = undefined;
	if (rule.filterMinimumAttendees && rule.filterMaximumAttendees) {
		attendeesDescriptor = `${rule.filterMinimumAttendees}-${rule.filterMaximumAttendees} invitees`;
	} else if (rule.filterMinimumAttendees) {
		const maybePlural = rule.filterMinimumAttendees === 1 ? "invitee" : "invitees";
		attendeesDescriptor = `${rule.filterMinimumAttendees} or more ${maybePlural}`;
	} else if (rule.filterMaximumAttendees) {
		const maybePlural = rule.filterMaximumAttendees === 1 ? "invitee" : "invitees";
		attendeesDescriptor = `${rule.filterMaximumAttendees} or less ${maybePlural}`;
	}

	let keywordsToContain = "";
	if (rule.filterKeywordsContains) {
		keywordsToContain = `${rule.filterKeywordsContains.join(", ")}`;
	}

	let keywordsToRestrict = "";
	if (rule.filterKeywordsRestrict) {
		keywordsToRestrict = `${rule.filterKeywordsRestrict.join(", ")}`;
	}

	const tooltips = useAutoRecordRuleDescriptors(rule, accountRef);

	return (
		<span className={className ?? styles.ruleDescription}>
			<Tooltip content={tooltips.attendeeType} className={styles.tooltip}>
				<span className={styles.emphasize}>{attendeeType}</span>
			</Tooltip>
			{" meetings "}
			{tooltips.organizer && rule.filterOrganizer === "SELF" && (
				<>
					{" "}
					<Tooltip content={tooltips.organizer}>
						<span className={styles.emphasize}>{"organized by me"}</span>
					</Tooltip>{" "}
				</>
			)}
			{attendeesDescriptor && (
				<>
					{" with "}
					<Tooltip content={tooltips.attendeesDescriptor}>
						<span className={styles.emphasize}>{attendeesDescriptor}</span>
					</Tooltip>
				</>
			)}
			{keywordsToContain && (
				<>
					{" that contain "}
					<Tooltip content={tooltips.keywordsToContain}>
						<span className={styles.emphasize}>{keywordsToContain}</span>
					</Tooltip>
				</>
			)}
			{keywordsToRestrict && (
				<>
					{" and does not contain "}
					<Tooltip content={tooltips.keywordsToRestrict}>
						<span className={styles.emphasize}>{keywordsToRestrict}</span>
					</Tooltip>
				</>
			)}
		</span>
	);
}

function UserPill({
	autoRecordRuleUser: autoRecordRuleUserRef,
}: {
	autoRecordRuleUser: AutoRecordRulesListItemUserPill$key;
}) {
	const userPillFragment = graphql`
		fragment AutoRecordRulesListItemUserPill on AutoRecordRuleUser {
			includeAllReports
			user {
				id
				fullName
				image
				email
				isGuest
				internalAttributes {
					allReports {
						edges {
							node {
								fullName
							}
						}
					}
				}
			}
		}
	`;

	const autoRecordRuleUser = useFragment<AutoRecordRulesListItemUserPill$key>(userPillFragment, autoRecordRuleUserRef);

	if (!autoRecordRuleUser || !autoRecordRuleUser.user) {
		return null;
	}

	const allReportsNames = autoRecordRuleUser.user.internalAttributes?.allReports?.edges.map(
		(edge: {
			node: {
				fullName: string;
			};
		}) => edge.node.fullName,
	);

	const tooltip =
		autoRecordRuleUser.includeAllReports && allReportsNames && allReportsNames.length > 0
			? `Includes: ${allReportsNames.join(", ")}`
			: undefined;

	const content = (
		<div className={styles.userPill}>
			<Avatar user={autoRecordRuleUser.user} size="small" />
			{autoRecordRuleUser.user.fullName}
			{autoRecordRuleUser.includeAllReports && <> + all reports</>}
			{autoRecordRuleUser.includeAllReports && autoRecordRuleUser.user?.internalAttributes?.allReports && (
				<span className={styles.numberOfReports}>
					{autoRecordRuleUser.user.internalAttributes.allReports.edges.length}
				</span>
			)}
		</div>
	);

	if (tooltip) {
		return <Tooltip content={<div className={styles.center}>{tooltip}</div>}>{content}</Tooltip>;
	} else {
		return content;
	}
}

const WorkspacePill = ({ account: accountRef }: { account: AutoRecordRulesListItemWorkspacePill$key }) => {
	const workspacePillFragment = graphql`
		fragment AutoRecordRulesListItemWorkspacePill on Account {
			id
			name
			logo
		}
	`;

	const account = useFragment(workspacePillFragment, accountRef);

	if (!account) {
		return null;
	}
	return (
		<div className={styles.userPill}>
			<img src={account.logo} alt={account.name} className={styles.workspacePillImage} />
			{account.name}
		</div>
	);
};

export function AppliesTo({
	account: accountRef,
	rule: ruleRef,
}: {
	account: AutoRecordRulesListItemWorkspacePill$key;
	rule: AutoRecordRulesListItemAppliesTo$key;
}) {
	const appliesToUsersFragment = graphql`
		fragment AutoRecordRulesListItemAppliesTo on AutoRecordRule {
			id
			ruleLevel
			autoRecordRuleUsers {
				user {
					id
				}
				...AutoRecordRulesListItemUserPill
			}
		}
	`;

	const rule = useFragment<AutoRecordRulesListItemAppliesTo$key>(appliesToUsersFragment, ruleRef);

	if (rule.ruleLevel === "WORKSPACE") {
		return (
			<div className={styles.appliesTo}>
				Applies to: <WorkspacePill account={accountRef} />
			</div>
		);
	} else {
		return (
			<div className={styles.appliesTo}>
				<div className={styles.userList}>
					Applies to:{" "}
					{rule.autoRecordRuleUsers.map(user => user && <UserPill key={user.user.id} autoRecordRuleUser={user} />)}
				</div>
			</div>
		);
	}
}

function RuleDeletionConfirmModal({
	rule: ruleRef,
	onClose,
	refetch,
}: {
	rule: AutoRecordRulesListItemRuleDeletionConfirmModal$key;
	onClose: () => void;
	refetch: () => void;
}) {
	const AutoRecordRulesListItemRuleDeletionConfirmModalFragment = graphql`
		fragment AutoRecordRulesListItemRuleDeletionConfirmModal on AutoRecordRule {
			id
			ruleType
		}
	`;

	const rule = useFragment<AutoRecordRulesListItemRuleDeletionConfirmModal$key>(
		AutoRecordRulesListItemRuleDeletionConfirmModalFragment,
		ruleRef,
	);

	const AutoRecordRulesListItemDeleteAutoRecordRuleMutation = graphql`
		mutation AutoRecordRulesListItemDeleteAutoRecordRuleMutation($id: ID!) {
			deleteAutoRecordRule(input: { id: $id }) {
				success
			}
		}
	`;

	const deleteRule = useCallback(() => {
		onClose();
		commitMutationPromise<AutoRecordRulesListItemDeleteAutoRecordRuleMutation>(environment, {
			mutation: AutoRecordRulesListItemDeleteAutoRecordRuleMutation,
			variables: {
				id: rule.id,
			},
		}).then((response: AutoRecordRulesListItemDeleteAutoRecordRuleMutation$data) => {
			if (response?.deleteAutoRecordRule?.success) {
				toast.success(`${rule.ruleType === "RECORD" ? "Recording rule" : "Recording rule"} deleted`);
				refetch();
			} else {
				toast.error(`Failed to delete ${rule.ruleType === "RECORD" ? "recording rule" : "recording restriction"}`);
			}
		});
	}, [onClose, AutoRecordRulesListItemDeleteAutoRecordRuleMutation, rule.id, rule.ruleType, refetch]);

	// Workaround for auto-focus on nested modals
	const deleteButtonRef = useRef<HTMLButtonElement>(null);
	const onAnimationStart = useCallback(() => {
		if (deleteButtonRef.current) {
			deleteButtonRef.current.focus();
		}
	}, []);

	return (
		<Modal onClose={onClose} onAnimationStart={onAnimationStart}>
			<Modal.Header noBottomBorder onClose={onClose}>
				<Modal.Title>Delete {rule.ruleType === "RECORD" ? "recording rule?" : "recording restriction?"}</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<p>This cannot be undone.</p>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="destructive" onClick={deleteRule} ref={deleteButtonRef} autoFocus>
					Delete
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

const useDeleteRule = (rule: AutoRecordRulesListItemRuleDeletionConfirmModal$key, refetch: () => void) => {
	const modals = useModals();

	const openDeleteModal = useCallback(
		() => modals?.open(({ close }) => <RuleDeletionConfirmModal rule={rule} onClose={close} refetch={refetch} />),
		[modals, rule, refetch],
	);

	return openDeleteModal;
};

const useDuplicateRule = (ruleRef: AutoRecordRulesListItemDuplicateAutoRecordRule$key, refetch: () => void) => {
	const AutoRecordRulesListItemDuplicateAutoRecordRuleFragment = graphql`
		fragment AutoRecordRulesListItemDuplicateAutoRecordRule on AutoRecordRule {
			id
			ruleType
		}
	`;

	const rule = useFragment<AutoRecordRulesListItemDuplicateAutoRecordRule$key>(
		AutoRecordRulesListItemDuplicateAutoRecordRuleFragment,
		ruleRef,
	);

	const AutoRecordRulesListItemDuplicateAutoRecordRuleMutation = graphql`
		mutation AutoRecordRulesListItemDuplicateAutoRecordRuleMutation($id: ID!) {
			duplicateAutoRecordRule(input: { id: $id }) {
				success
			}
		}
	`;

	const duplicateRule = useCallback(() => {
		commitMutationPromise<AutoRecordRulesListItemDuplicateAutoRecordRuleMutation>(environment, {
			mutation: AutoRecordRulesListItemDuplicateAutoRecordRuleMutation,
			variables: {
				id: rule.id,
			},
		}).then(() => {
			toast.success(`${rule.ruleType === "RECORD" ? "Auto-record policy" : "Recording restriction"} duplicated`);
			refetch();
		});
	}, [AutoRecordRulesListItemDuplicateAutoRecordRuleMutation, rule.id, rule.ruleType, refetch]);

	return duplicateRule;
};

export function AutoRecordRulesListItem({
	rule: ruleRef,
	account: accountRef,
	refetch,
	secondary,
}: AutoRecordRulesListItemProps) {
	const autoRecordRulesListItemFragment = graphql`
		fragment AutoRecordRulesListItemRule on AutoRecordRule {
			id
			ruleType
			ruleLevel
			filterAttendeeType
			filterMinimumAttendees
			filterMaximumAttendees
			filterKeywordsContains
			filterKeywordsRestrict
			filterOrganizer

			...AutoRecordRulesListItemAppliesTo
			...AutoRecordRulesListItemRuleDeletionConfirmModal
			...AutoRecordRulesListItemDuplicateAutoRecordRule
			...CreateAndEditModalRule
		}
	`;

	const rule = useFragment<AutoRecordRulesListItemRule$key>(autoRecordRulesListItemFragment, ruleRef);

	const [isMenuOpen, setIsMenuOpen] = React.useState(false);
	const deleteRule = useDeleteRule(rule, refetch);
	const duplicateRule = useDuplicateRule(rule, refetch);
	const { open: edit } = useOpenEditModal(rule, refetch, accountRef);

	const onEdit = useCallback(() => {
		setIsMenuOpen(false);
		edit();
	}, [edit]);

	return (
		<div className={cx(styles.ruleCard, secondary ? styles.ruleCardDark : styles.ruleCardLight)}>
			<div className={styles.ruleCardContent}>
				<AutoRecordRuleDescription rule={rule} account={accountRef} />
				{rule.ruleLevel !== "USER" && <AppliesTo rule={rule} account={accountRef} />}
			</div>

			<Menu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
				<Menu.Trigger>
					<button className={styles.threeDotsButton}>
						<FontAwesomeIcon icon={faEllipsisV} />
					</button>
				</Menu.Trigger>
				<Menu.Content>
					<Menu.Item icon={faPencil} onSelect={onEdit}>
						Edit
					</Menu.Item>
					<Menu.Item icon={faCopy} onSelect={duplicateRule}>
						Duplicate
					</Menu.Item>
					<Menu.Separator />

					<Menu.Item icon={faTrashCan} onSelect={deleteRule} destructive>
						Delete
					</Menu.Item>
				</Menu.Content>
			</Menu>
		</div>
	);
}
